import { SecurityColumnVisibility } from '../../../../features/clients/client/portfolio/overview/store/types';
import { PortfolioPreferences, PreferenceUpgrader } from '../../types';

/**
 * System default column visibility. This is used as both the initial value for
 * new users (via migration step 1) and the reset target when a user resets
 * columns to defaults.
 *
 * Adding a new column:
 *
 * 1. Add the new optional column to the SecurityColumnVisibility type
 *    e.g. `myNewField?: boolean`
 *
 * 2. Add the new column here with its default visibility (true = shown, false = hidden).
 *
 * 3. Add a migration step to add the column to existing users' saved preferences:
 *
 *    Step: (old) => ({
 *      ...old,
 *      defaultColumns: {
 *        ...old.defaultColumns,
 *        myNewField: old.defaultColumns?.myNewField ?? true,
 *      },
 *      version: 3,
 *    }),
 *
 * 4. Bump latestVersion (e.g. latestVersion: 3).
 *
 * 5. Register the column in columnChangeDetection.ts `columnsIntroducedAtVersion`
 *    so existing users are notified about the new column:
 *
 *    columnsIntroducedAtVersion = { 3: ['myNewField'] }
 *
 * 6. Update unit tests.
 *
 * Removing a column:
 *
 * 1. Remove the column from SecurityColumnVisibility type.
 * 2. Remove it from this object.
 * 3. Register in columnChangeDetection.ts `columnsRemovedAtVersion` so users
 *    are notified about the removal.
 *
 * The change detection uses the user's pre-migration version number to determine
 * which columns are new or removed. See columnChangeDetection.ts for details.
 */
export const defaultColumnVisibility: SecurityColumnVisibility = {
  name: true,
  securityCode: true,
  units: true,
  costBase: true,
  unitCost: true,
  unitPrice: true,
  nativePrice: false,
  currentValue: true,
  nativeValue: false,
  currentWeightPercentage: true,
  gainLossPercentage: true,
  returnSinceInception: true,
  returnSinceInceptionPercentage: true,
  gainLossValue: true,
  estimatedIncome: true,
  estimatedYield: true,
  estimatedGrossIncome: false,
  estimatedGrossYield: false,
  priceDate: true,
  currencyCode: false,
  forexRate: false,
};

const portfolioPreferencesMigration: PreferenceUpgrader<PortfolioPreferences> = {
  latestVersion: 2,
  steps: {
    // v0 -> v1: introduce `defaultView` with a 'compact' default when it has not been set.
    0: (old) => ({ ...old, defaultView: old.defaultView ?? 'compact', version: 1 }),

    // v1 -> v2: introduce `defaultColumns` with the standard column visibility defaults.
    1: (old) => ({ ...old, defaultColumns: old.defaultColumns ?? defaultColumnVisibility, version: 2 }),
  },
};

export default portfolioPreferencesMigration;
